/**
 * Helpers de lecture du champ fact_id_5000 des opérations 8000 — COMPTA-219.
 *
 * fact_id_5000 n'est pas un identifiant unique mais une liste d'écritures 5000
 * séparées par ", " : une même facture peut couvrir plusieurs ventes, et une même
 * vente être rapprochée de plusieurs écritures. Des données historiques portent en
 * plus un préfixe ", " hérité d'un bug de concaténation côté storehouse.
 */

/** Découpe fact_id_5000 en identifiants, en ignorant les entrées vides. */
export function parseFactIdList(raw) {
    if (!raw || typeof raw !== 'string') return [];
    return raw
        .split(',')
        .map((id) => id.trim())
        .filter(Boolean);
}

/**
 * Premier identifiant d'écriture 5000 d'une opération, ou "" si elle n'est pas pointée.
 * À utiliser partout où l'API attend un income_id / spending_id unique : transmettre
 * la chaîne brute ne correspondait à aucune écriture côté back.
 */
export function primaryFactId(raw) {
    const [first = ''] = parseFactIdList(raw);
    return first;
}

/**
 * Montant qu'un dépointage doit recréditer à l'écriture 5000 : le TTC, puisque
 * c'est ce que le pointage a débité (le montant à pointer est pré-rempli avec
 * total_available, initialisé au TTC à la création de l'opération). Repli sur le
 * HT pour les opérations antérieures à l'introduction du TTC.
 */
export function linkedAmount(operation) {
    if (!operation) return 0;
    const ttc = parseFloat(operation.total_price_TTC);
    if (Number.isFinite(ttc) && ttc > 0) return ttc;
    return parseFloat(operation.total_price) || 0;
}

/**
 * Montant rapproché d'une écriture comptable, déduit de son reste à pointer.
 *
 * Sert aux deux sens du rapprochement, qui portent des noms de champs différents :
 * recettes (income_amount / income_total_available, rapprochées avec les ventes) et
 * dépenses (spending_amount / spending_rest_to_match, rapprochées avec les achats et
 * les réapprovisionnements externes).
 *
 * Renvoie null si l'un des deux montants est inexploitable : présenter le montant total
 * comme rapproché sur une écriture qui ne l'est pas serait trompeur.
 */
export function reconciledAmount(total, remaining) {
    const amount = parseFloat(total);
    const rest = parseFloat(remaining);
    if (!Number.isFinite(amount) || !Number.isFinite(rest)) return null;
    return amount - rest;
}

/**
 * Montant déjà rapproché avec la compta 5000, déduit du reste à pointer (total_available).
 *
 * Renvoie null lorsque le reste à pointer est incohérent avec le montant de l'opération —
 * négatif, ou supérieur au total. Ces cas existent en base : le solde était décrémenté même
 * quand le rapprochement échouait côté 8000. Afficher un montant calculé sur une valeur
 * fausse serait pire que de ne rien afficher.
 *
 * Une opération marquée rapprochée l'est pour la totalité de son montant, quel que soit
 * l'état de total_available.
 */
export function pointedAmount(operation) {
    if (!operation) return null;
    const total = linkedAmount(operation);
    if (!total) return null;
    if (isOperationMatched(operation)) return total;

    const remaining = parseFloat(operation.total_available);
    if (!Number.isFinite(remaining) || remaining < 0 || remaining > total) return null;
    return total - remaining;
}

/** Vrai si l'opération est intégralement rapprochée, quel que soit le modèle de données. */
export function isOperationMatched(operation) {
    if (!operation) return false;
    // matched : modèle TransferCB actuel ; sale_matched : anciennes ventes (typesNew).
    return Boolean(operation.matched || operation.sale_matched);
}
